import type { StatusTone } from '@beonit/adaptive-raindrop';
import {
  type AnalysisRun,
  type GroupBatch,
  type GroupMember,
  type GroupMetrics,
  isRunFinished,
  type MethodDelta,
  type MetricsSnapshot,
  type RunFailure,
  type RunStatus,
} from '../model.js';
import { productFigure } from './metrics-reading.js';
import { SERIES_METRICS } from './metrics-series.js';
import { failedFollowUp } from './run-choice.js';
import { runFailureReason } from './run-failure.js';

/**
 * A group is read by its latest execution, and only by it.
 *
 * The product owner's rule (2026-09-24): «solo la última. Para los grupos de análisis, para los
 * vídeos, para todo». The numbers of a group are the numbers of its last batch; a member that
 * failed there is left out of them rather than scored with a good run from an older batch, and a
 * member the batch did not include is not in them at all. Nothing here computes a percentage:
 * Virtual Profiler measures one batch when asked for it by name (`metrics?batch=`), and this file
 * only decides which batch that is and what the screen says while it cannot be measured yet.
 */

/**
 * The executions of a group, newest first.
 *
 * Ordered here rather than trusted from the list: upstream sends them newest first and a fake once
 * put the running one last, and «the latest execution» must not depend on who answered. One that
 * never started goes last — it has no moment to be the latest at.
 */
export function batchesNewestFirst(
  batches: readonly GroupBatch[] | null | undefined,
): GroupBatch[] {
  return [...(batches ?? [])]
    .map((batch, index) => ({ batch, index }))
    .sort((a, b) => {
      const left = a.batch.startedAt?.getTime() ?? Number.NEGATIVE_INFINITY;
      const right = b.batch.startedAt?.getTime() ?? Number.NEGATIVE_INFINITY;

      return right - left || a.index - b.index;
    })
    .map(({ batch }) => batch);
}

/**
 * The execution a group's numbers come from, or null when it has never run.
 *
 * A cancelled execution is skipped, whether its runs have stopped yet or not: it is not a
 * measurement (product owner, 2026-09-25) and upstream writes none for it, so the numbers stay on
 * the execution before it. It is still in the history — `batchesNewestFirst` keeps it.
 */
export function latestBatch(batches: readonly GroupBatch[] | null | undefined): GroupBatch | null {
  return batchesNewestFirst(batches).find((batch) => !batch.cancelled) ?? null;
}

/**
 * What the latest execution can say right now.
 *
 * - `never_run`: there is none, so there are no numbers — not zeros.
 * - `running`: some of its runs have not finished, automatic retries included. No numbers either:
 *   a batch measured halfway changes every time somebody reloads, and showing the previous one
 *   instead would present old figures as the latest.
 * - `measured`: every run of it finished, and Virtual Profiler can measure it.
 */
export type LatestExecutionState =
  { kind: 'never_run' } | { kind: 'running'; done: number; total: number } | { kind: 'measured' };

const FINISHED: readonly RunStatus[] = ['SUCCEEDED', 'FAILED', 'CANCELLED'];

/**
 * The state of the latest execution, with its progress while it runs.
 *
 * The progress is counted over the members as that batch left them (`members?batch=`) when they
 * are at hand: one per video, each by its latest run there. The batch's count of videos is the
 * fallback, and its attempts the last resort, from an API that does not count videos — a video
 * that failed and is being retried is two runs in them.
 */
export function latestExecutionState(
  batch: GroupBatch | null,
  members?: readonly Pick<GroupMember, 'resolved'>[] | null,
): LatestExecutionState {
  if (!batch) return { kind: 'never_run' };
  if (batch.complete) return { kind: 'measured' };

  const inBatch = (members ?? []).filter((member) => member.resolved.bucket !== 'never_run');

  if (inBatch.length > 0) {
    const done = inBatch.filter(
      (member) => member.resolved.bucket === 'ok' || member.resolved.bucket === 'not_analysed',
    ).length;

    return { kind: 'running', done, total: inBatch.length };
  }

  const byStatus = batch.membersByStatus ?? batch.runsByStatus;
  const done = FINISHED.reduce((sum, status) => sum + (byStatus[status] ?? 0), 0);

  return { kind: 'running', done, total: batch.members ?? batch.runs };
}

/**
 * What the front page shows of a group when nobody picked a version.
 *
 * The group screen's reading, with what the front page has: its latest execution, or nothing.
 *
 * - `never_run` / `running`: no numbers — half an execution is not a measurement;
 * - `version`: the measurement that execution left when it finished, the automatic one. A manual
 *   one tagged with it may predate the rule and score the group across executions;
 * - `live`: it finished and its measurement is not written yet — a moment after it ends, or the
 *   reconciliation minutes later — so its numbers are asked for by its label.
 *
 * Never the newest measurement of another execution, nor the reading without one, which borrows
 * each member's last good run from any execution and counts a failure as a miss.
 */
export type OverviewGroupReading =
  | { kind: 'never_run' }
  | { kind: 'running'; done: number; total: number }
  | { kind: 'version'; snapshotId: string }
  | { kind: 'live'; batch: string };

export function overviewGroupReading(
  latest: GroupBatch | null,
  versions: readonly Pick<MetricsSnapshot, 'snapshotId' | 'trigger' | 'batch'>[],
): OverviewGroupReading {
  const state = latestExecutionState(latest);

  if (!latest || state.kind === 'never_run') return { kind: 'never_run' };
  if (state.kind === 'running') return state;

  const measured = versions.find(
    (version) => version.trigger === 'auto' && version.batch === latest.batch,
  );

  return measured
    ? { kind: 'version', snapshotId: measured.snapshotId }
    : { kind: 'live', batch: latest.batch };
}

/** One headline of the latest execution: a product metric and upstream's number for it. */
export interface ExecutionHeadline {
  key: string;
  label: string;
  /** Null when the method has no figure for it, which is not 0 %. */
  value: number | null;
}

/** In the reading in force: normal, or the «Métricas globales» the user chose. */
function headlineOf(block: MethodDelta | undefined, metricKey: string): number | null {
  return productFigure(block, metricKey);
}

/**
 * The three headlines of one execution under one score method, read as Virtual Profiler sent them.
 *
 * Null unless the answer is about that execution. An upstream that ignored `?batch=` answers with
 * its older reading across executions, and those numbers under «Última ejecución» would be exactly
 * the mix the rule forbids.
 */
export function executionHeadlines(
  metrics: Pick<GroupMetrics, 'batch' | 'methods'> | null | undefined,
  batch: string,
  method: string,
): ExecutionHeadline[] | null {
  if (!metrics || metrics.batch !== batch) return null;

  const block = metrics.methods[method];

  return SERIES_METRICS.map((definition) => ({
    key: definition.key,
    label: definition.label,
    value: headlineOf(block, definition.key),
  }));
}

/**
 * Why a new execution cannot be launched now, or null when it can.
 *
 * A busy group is never a reason (product owner, 2026-09-26: «un grupo se tiene que poder poner en
 * ejecución varias veces»). While anything of it is in flight — its latest execution, a cancelled
 * one still stopping, a retry — Virtual Profiler records the new execution as waiting and starts it,
 * whole, once the one before it has finished; `lib/group-launch.ts` says so before the launch. Only a
 * group without videos, or a launch already on its way (a double click), holds the button.
 */
export function newExecutionBlocker(input: { members: number; launching: boolean }): string | null {
  if (input.launching) return 'Lanzando la ejecución…';
  if (input.members === 0) return 'El grupo no tiene vídeos: añade alguno antes de ejecutarlo';

  return null;
}

/**
 * What a row of the history says it ran.
 *
 * By video when upstream counts them, each by its latest run there — the way that execution's
 * metrics count it, so a failure its automatic retry healed is not «con error» beside KPIs that
 * scored the video. By attempt only from an API that does not, and named as attempts, with the
 * retries beside them so the row does not read as more videos than it had.
 */
export function batchTally(
  batch: Pick<GroupBatch, 'runs' | 'runsByStatus' | 'members' | 'membersByStatus'>,
  retries: number,
): string {
  const retried =
    retries > 0
      ? `; ${retries} ${retries === 1 ? 'reintento automático' : 'reintentos automáticos'}`
      : '';
  const byVideo = batch.members !== null && batch.membersByStatus !== null;
  const counts = (byVideo ? batch.membersByStatus : batch.runsByStatus) ?? {};
  const detail = `(${counts.SUCCEEDED ?? 0} ok, ${counts.FAILED ?? 0} con error${retried})`;

  if (byVideo) {
    const videos = batch.members ?? 0;

    return `${videos} ${videos === 1 ? 'vídeo' : 'vídeos'} ${detail}`;
  }

  return `${batch.runs} ${batch.runs === 1 ? 'ejecución' : 'ejecuciones'} ${detail}`;
}

/** A chip saying how a member stands in the latest execution. */
export interface LatestRunChip {
  key: string;
  text: string;
  tone: StatusTone;
  /** Upstream's words for why it failed, on the chip that says it did. */
  title?: string;
}

/**
 * How a member stands in the latest execution, for the members table.
 *
 * Read from `members?batch=<latest>`, so the chips and the headlines are one reading. A failed run
 * says what it means for the numbers — left out of them — because the older rule counted it as a
 * miss and somebody who learnt that one would read the chip the old way. The flags only go on a
 * member that counts: a degraded failure is still a failure.
 *
 * A failure also says which step failed (`lib/run-failure.ts`), from the error of the member's run
 * in the ledger — the members' resolution does not carry it.
 */
export function latestRunChips(
  resolution: Pick<GroupMember['resolved'], 'bucket' | 'status' | 'degraded' | 'notRequested'>,
  groupHasRun: boolean,
  retry: MemberRetry = null,
  error: RunFailure | null = null,
): LatestRunChip[] {
  if (!groupHasRun) return [{ key: 'never_run', text: 'Sin ejecutar', tone: 'neutral' }];

  const chips = withFailure(bucketChips(resolution), resolution, error);
  const followUp = retryChip(resolution.bucket, retry);

  return followUp ? [...chips, followUp] : chips;
}

/**
 * The step a failed member failed at, beside the chip that says it failed.
 *
 * A failure this build has no name for gets no chip of its own — «worker_lost» in a chip reads as
 * noise — only upstream's code and words as the title of the failed chip.
 */
function withFailure(
  chips: LatestRunChip[],
  resolution: Pick<GroupMember['resolved'], 'bucket' | 'status'>,
  error: RunFailure | null,
): LatestRunChip[] {
  const reason = runFailureReason(error);

  if (!reason || resolution.bucket !== 'not_analysed' || resolution.status === 'CANCELLED') {
    return chips;
  }

  if (!reason.known) {
    return chips.map((chip, index) => (index === 0 ? { ...chip, title: reason.detail } : chip));
  }

  const [failed, ...rest] = chips;

  return [
    ...(failed ? [failed] : []),
    {
      key: 'failure',
      text: reason.short,
      tone: 'danger',
      title: `${reason.label}: ${reason.detail}`,
    },
    ...rest,
  ];
}

function bucketChips(
  resolution: Pick<GroupMember['resolved'], 'bucket' | 'status' | 'degraded' | 'notRequested'>,
): LatestRunChip[] {
  switch (resolution.bucket) {
    case 'ok': {
      const chips: LatestRunChip[] = [{ key: 'ok', text: 'Analizado', tone: 'success' }];

      if (resolution.degraded) chips.push({ key: 'degraded', text: 'Degradado', tone: 'warning' });
      if (resolution.notRequested) {
        chips.push({ key: 'not_requested', text: 'Sin veracidad', tone: 'neutral' });
      }

      return chips;
    }
    case 'not_analysed':
      return [
        {
          key: 'not_analysed',
          text:
            resolution.status === 'CANCELLED'
              ? 'Cancelado — fuera de la métrica'
              : 'Falló — fuera de la métrica',
          tone: 'danger',
        },
      ];
    case 'pending':
      return [{ key: 'pending', text: 'En marcha', tone: 'info' }];
    // `never_run`, and whatever upstream adds tomorrow: the conservative reading is «not in it».
    default:
      return [{ key: 'never_run', text: 'No está en la última ejecución', tone: 'neutral' }];
  }
}

/**
 * What follows a member's latest run in the execution, when it did not succeed.
 *
 * - `retrying`: it is the automatic retry, still going;
 * - `will_retry`: it failed and upstream will retry it by itself, once;
 * - `failed_twice`: it failed and it was the retry, so nothing will relaunch it but a person;
 * - `needs_relaunch`: it failed and upstream will not retry it (too old, cancelled, nothing kept
 *   to replay), so the same;
 * - `fix_configuration`: it failed because of its configuration, which fails the same way on every
 *   retry and every replay, so nothing helps until somebody fixes the configuration.
 *
 * Read off the ledger's run, which the members' resolution does not carry.
 */
export type MemberRetry =
  'retrying' | 'will_retry' | 'failed_twice' | 'needs_relaunch' | 'fix_configuration' | null;

export function memberRetryState(
  run:
    | Pick<
        AnalysisRun,
        | 'status'
        | 'trigger'
        | 'sourceType'
        | 'cancellation'
        | 'hasStoredJob'
        | 'finishedAt'
        | 'error'
      >
    | undefined,
  now: number,
): MemberRetry {
  if (!run) return null;
  if (!isRunFinished(run.status)) return run.trigger === 'auto_retry' ? 'retrying' : null;
  if (run.status !== 'FAILED') return null;

  const followUp = failedFollowUp(run, now);

  // Before «falló dos veces»: what helps a configuration error is the same on any attempt.
  if (followUp === 'fix_configuration') return 'fix_configuration';
  if (run.trigger === 'auto_retry') return 'failed_twice';

  return followUp === 'auto_retry' ? 'will_retry' : 'needs_relaunch';
}

/** The chip saying what follows, on the bucket it belongs to and no other. */
function retryChip(bucket: string, retry: MemberRetry): LatestRunChip | null {
  if (bucket === 'pending' && retry === 'retrying') {
    return { key: 'retrying', text: 'Reintento automático', tone: 'info' };
  }
  if (bucket !== 'not_analysed') return null;

  switch (retry) {
    case 'will_retry':
      return { key: 'will_retry', text: 'Se reintentará sola', tone: 'info' };
    case 'failed_twice':
      return { key: 'failed_twice', text: 'Falló dos veces', tone: 'warning' };
    case 'needs_relaunch':
      return { key: 'needs_relaunch', text: 'No se reintentará sola', tone: 'warning' };
    case 'fix_configuration':
      return { key: 'fix_configuration', text: 'Corrige la configuración', tone: 'warning' };
    default:
      return null;
  }
}

/**
 * How many automatic retries each execution holds, by its label.
 *
 * A retry keeps the batch of the run it repeats, so it is part of that execution and not one of
 * its own — which is also why the execution's counts can hold one FAILED and one SUCCEEDED for a
 * single video.
 */
export function retriesByBatch(
  runs: readonly Pick<AnalysisRun, 'trigger' | 'batch'>[] | null | undefined,
): Map<string, number> {
  const counts = new Map<string, number>();

  for (const run of runs ?? []) {
    if (run.trigger !== 'auto_retry' || !run.batch) continue;

    counts.set(run.batch, (counts.get(run.batch) ?? 0) + 1);
  }

  return counts;
}
